import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileText,
  Download,
  RefreshCw,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Receipt,
  Calculator,
  DollarSign,
  FileDown,
  Info,
  TrendingUp,
  ArrowDownToLine
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxCentrePage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [taxYears, setTaxYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthlyDetails, setMonthlyDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  // Fetch available tax years
  const fetchTaxYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tax/years`, { headers });
      setTaxYears(response.data);
      if (response.data.length > 0 && !selectedYear) {
        setSelectedYear(response.data[0].year);
      }
    } catch (error) {
      console.error('Failed to fetch tax years:', error);
    }
  };

  // Fetch data for selected year
  const fetchYearData = useCallback(async (year) => {
    if (!year) return;
    
    setLoadingData(true);
    try {
      const [certsRes, summaryRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/tax/${year}/certificates`, { headers }),
        axios.get(`${API_URL}/api/tax/${year}/summary`, { headers }),
        axios.get(`${API_URL}/api/tax/${year}/monthly`, { headers })
      ]);
      
      setCertificates(certsRes.data);
      setSummary(summaryRes.data);
      setMonthlyDetails(monthlyRes.data);
    } catch (error) {
      console.error('Failed to fetch tax data:', error);
    } finally {
      setLoadingData(false);
    }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchTaxYears();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchYearData(selectedYear);
    }
  }, [selectedYear, fetchYearData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount || 0).replace('ZAR', 'R');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'available':
      case 'published':
        return { label: 'Available', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'processing':
        return { label: 'Processing', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      case 'not_due':
        return { label: 'Not Yet Due', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Calendar };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertCircle };
    }
  };

  const handleDownloadCertificate = async (cert) => {
    if (!['available', 'published'].includes(cert.status)) {
      toast.info('Certificate is not yet available for download.');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/tax/${selectedYear}/certificate/${cert.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cert.file_name || `IT3B_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const handleDownloadSummary = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/tax/${selectedYear}/summary/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_summary_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Summary downloaded successfully');
    } catch (error) {
      toast.error('Failed to download summary');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/tax/${selectedYear}/monthly/export/csv`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `withholding_tax_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const it3bCert = certificates.find(c => c.certificate_type === 'IT3B');
  const it3bStatus = it3bCert ? getStatusConfig(it3bCert.status) : null;
  const IT3BStatusIcon = it3bStatus?.icon || Calendar;

  const selectedYearData = taxYears.find(y => y.year === selectedYear);

  // Calculate totals from monthly data for display
  const monthlyTotals = monthlyDetails.reduce((acc, m) => ({
    gross: acc.gross + (m.gross_interest || 0),
    wht: acc.wht + (m.wht_amount || 0),
    net: acc.net + (m.net_interest || 0)
  }), { gross: 0, wht: 0, net: 0 });

  return (
    <div className="space-y-6" data-testid="tax-centre-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Tax Centre</h1>
          <p className="text-[#6C757D] mt-1">View and download your tax certificates and summaries</p>
        </div>
        <div className="flex items-center gap-3">
          {loading ? (
            <Skeleton className="h-10 w-48" />
          ) : (
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-64 border-gray-200" data-testid="tax-year-selector">
                <Calendar className="h-4 w-4 mr-2 text-[#6C757D]" />
                <SelectValue placeholder="Select tax year" />
              </SelectTrigger>
              <SelectContent>
                {taxYears.map((year) => (
                  <SelectItem key={year.year} value={year.year}>
                    {year.label}
                    {year.status === 'current' && (
                      <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Current</Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchYearData(selectedYear)}
            className="border-gray-200"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loadingData ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : (
        <>
          {/* Section 1: Tax Certificates */}
          <Card className="border-0 shadow-sm" data-testid="tax-certificates-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#1D2D2A]/10 flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-[#1D2D2A]" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Tax Certificates</CardTitle>
                    <CardDescription>Official certificates submitted to SARS</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {it3bCert && (
                <div className="border rounded-lg p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${it3bStatus.color}`}>
                        <IT3BStatusIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-[#212529]">IT3(b) Interest Income Certificate</h3>
                          <Badge className={`${it3bStatus.color} border`}>
                            {it3bStatus.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-[#6C757D] mb-2">
                          Official certificate reflecting interest income earned during tax year {selectedYear}
                        </p>
                        {it3bCert.generated_date && (
                          <p className="text-xs text-[#6C757D]">
                            Generated: {formatDate(it3bCert.generated_date)}
                          </p>
                        )}
                        {it3bCert.notes && (
                          <p className="text-xs text-[#ADB5BD] mt-1 italic">{it3bCert.notes}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      className={`${
                        ['available', 'published'].includes(it3bCert.status)
                          ? 'bg-[#1D2D2A] hover:bg-[#243D37] text-white' 
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={() => handleDownloadCertificate(it3bCert)}
                      disabled={!['available', 'published'].includes(it3bCert.status)}
                      data-testid="download-it3b-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Info Note */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">About IT3(b) Certificates</p>
                  <p className="text-blue-700 mt-1">
                    The IT3(b) certificate is submitted directly to SARS and reflects all interest income 
                    earned during the tax year. This certificate is typically available by June following 
                    the end of the tax year.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Annual Tax Summary */}
          <Card className="border-0 shadow-sm" data-testid="annual-summary-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Calculator className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Annual Tax Summary</CardTitle>
                    <CardDescription>Investor-friendly breakdown of your tax year</CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSummary}
                  className="border-gray-200"
                  data-testid="download-summary-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Gross Interest */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-green-700">Gross Interest Earned</span>
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-800" data-testid="gross-interest">
                      {formatCurrency(summary.gross_interest)}
                    </p>
                  </div>

                  {/* Withholding Tax */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-amber-700">Withholding Tax Deducted</span>
                      <Receipt className="h-4 w-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-amber-800" data-testid="withholding-tax">
                      {formatCurrency(summary.withholding_tax)}
                    </p>
                  </div>

                  {/* Net Interest */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-700">Net Interest Received</span>
                      <DollarSign className="h-4 w-4 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-800" data-testid="net-interest">
                      {formatCurrency(summary.net_interest)}
                    </p>
                  </div>

                  {/* Capital Repayments */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Capital Repayments</span>
                      <ArrowDownToLine className="h-4 w-4 text-gray-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-700" data-testid="capital-returned">
                      {formatCurrency(summary.capital_returned)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Not taxable</p>
                  </div>

                  {/* Fees */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Fees & Charges</span>
                      <FileText className="h-4 w-4 text-gray-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-700" data-testid="fees">
                      {formatCurrency(summary.fees)}
                    </p>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-start gap-3">
                <Info className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">
                  This is an investor-friendly summary for your records. For official tax purposes, 
                  please refer to your IT3(b) certificate which is submitted directly to SARS.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Monthly Withholding Tax Breakdown */}
          <Card className="border-0 shadow-sm" data-testid="monthly-wht-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Withholding Tax Breakdown</CardTitle>
                    <CardDescription>Monthly breakdown of interest income and WHT</CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="border-gray-200"
                  data-testid="export-csv-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Gross Interest</TableHead>
                      <TableHead className="text-right">WHT Rate</TableHead>
                      <TableHead className="text-right">WHT Amount</TableHead>
                      <TableHead className="text-right">Net Interest</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyDetails.map((row, index) => (
                      <TableRow key={row.id || index} data-testid={`monthly-row-${row.month}`}>
                        <TableCell className="font-medium">{row.month_label}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.gross_interest)}</TableCell>
                        <TableCell className="text-right">{row.wht_rate}%</TableCell>
                        <TableCell className="text-right text-amber-600">{formatCurrency(row.wht_amount)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(row.net_interest)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-gray-50 font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right">{formatCurrency(monthlyTotals.gross)}</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right text-amber-600">{formatCurrency(monthlyTotals.wht)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(monthlyTotals.net)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Info Note */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg flex items-start gap-3">
                <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-800">
                  <p className="font-medium">About Withholding Tax (WHT)</p>
                  <p className="text-purple-700 mt-1">
                    A flat 15% withholding tax is deducted at source from interest payments to resident 
                    individuals. This tax is paid directly to SARS on your behalf. Certain exemptions may 
                    apply based on your tax status - please consult your tax advisor.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TaxCentrePage;
